import { useContext, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { CtPlatform, CtPlatformBaseItem } from 'models/general';
import { StoreContext, ImportSectionEnum } from 'models/import';
import ImportPlatformInstructions from 'pages/import/platform/instructions';
import { getLaminasApiData } from 'services/api';
import Alert from 'components/alert';
import ImportTable from 'components/table/importTable';
import ContentBlock, { ContentBlockModifier } from 'components/contentBlock';
import ApiCardDetail from 'pages/import/platform/apiCardDetail';
import ImportPlatformCardsComponent from 'pages/import/platform/cards';
import ImportPlatformFormComponent from 'pages/import/platform/form';
import ImportPlatformHeaderComponent from 'pages/import/platform/header';
import ImportPlatformOptionsComponent from 'pages/import/platform/options';
import ImportSteps from 'pages/import/shared/steps';

function ImportPlatformComponent() {
  const { type, id, wallet } = useParams();

  const {
    setStep, setPlatform, setBaseItems, setActiveSection, activeSection, platform,
  } = useContext(StoreContext);

  const stepsRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  const getPlatform = (_type: string, _id: string, _wallet: string) => getLaminasApiData<CtPlatform>(`/platform/detail?platform-type=${_type}&platform-name=${_id}&wallet-address=${_wallet}`);
  const getPlatformBaseItems = (_type: string, _id: string, _wallet: string) => getLaminasApiData<CtPlatformBaseItem[]>(`/import/list?platform-type=${_type}&platform-name=${_id}&wallet-address=${_wallet}`);

  useEffect(() => {
    const platformType = type || '';
    const platformId = id || '';
    const walletAddress = wallet ? encodeURIComponent(wallet) : '';

    Promise.all([
      getPlatform(platformType, platformId, walletAddress),
      getPlatformBaseItems(platformType, platformId, walletAddress),
    ])
      .then((res) => {
        const [platformRes, baseItemsRes] = res;
        const platformData = platformRes.data;
        const baseItemsData = baseItemsRes.data;

        setPlatform(platformData || {});
        setBaseItems(baseItemsData || []);
        setStep(1);
        setActiveSection({ data: ImportSectionEnum.CARDS, ref: undefined });
      }, () => { });
  }, []);

  // on destroy
  useEffect(() => () => {
    setPlatform({} as unknown as CtPlatform);
    setBaseItems([]);
    setStep(0);
    setActiveSection(undefined);
  }, []);

  return (
    <>
      <div className="ct_section _import" ref={sectionRef}>
        <div className="ct_section-inner">
          <div className="ct_section-main">
            {activeSection && activeSection.data < ImportSectionEnum.SUCCESS_API && (
              <>
                <ImportPlatformHeaderComponent />
                {activeSection.data >= ImportSectionEnum.CARDS && (
                <ImportPlatformCardsComponent />
                )}
                {activeSection.data >= ImportSectionEnum.OPTIONS && (
                <ImportPlatformOptionsComponent />
                )}
                {activeSection.data >= ImportSectionEnum.FORM && (
                <ImportPlatformFormComponent />
                )}
              </>
            )}
            {activeSection && activeSection.data === ImportSectionEnum.SUCCESS_API && (
              <>
                <Alert type="success" title={`${platform?.display_name || ''} API import was created succesfully and your trades are currently being imported`}>
                  <span>
                    It might take between 10 to 180 minutes to complete.
                    Once it&apos;s done, you can find your trades in the Overview page.
                  </span>
                </Alert>
                {/* TODO: Replace id with actual job ID or pass actual data from response */}
                <ApiCardDetail id={1234} />
              </>
            )}
          </div>
          <div className="ct_section-aside">
            <div style={{ position: 'sticky', top: '0', zIndex: 10 }} ref={stepsRef}>
              <ImportSteps />
            </div>
            <ImportPlatformInstructions
              stepsRef={stepsRef.current}
              sectionRef={sectionRef.current}
            />
          </div>
        </div>
      </div>

      {activeSection && activeSection.data === ImportSectionEnum.SUCCESS_API && (
      <div className="ct_section _padded">
        <div className="ct_section-inner">
          <div className="ct_section-main">
            <ContentBlock
              title="Overview"
              desc="This overview provides a list of your current imports grouped by your exchanges/wallets."
              modifiers={[ContentBlockModifier.MAIN]}
            >
              <ImportTable />
            </ContentBlock>
          </div>
        </div>
      </div>
      )}
    </>

  );
}

export default ImportPlatformComponent;
